import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SourceChip from '../../capability-setup/SourceChip';
import { sourceOf } from '../../capability-setup/capabilitySource';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import RemoveIcon from '@mui/icons-material/Remove';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import { fadeInSx } from '../../../../../components/ui/DashboardSkeletons';
import { BODY, GapSection } from '../../Program/components/learning-lifecycle/GapsRisksPanel';
import {
  HistoryTables,
  LEVEL_COLOR,
  LEVEL_NAME,
  LevelLabel,
  PreviousLevelsMark
} from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import CapabilityTrend from '../../Program/components/learning-lifecycle/CapabilityTrend';
import { trendFromApi } from '../../Program/components/learning-lifecycle/capabilityTrendData';
import {
  ChartSection,
  ScoreCard,
  ScoreLine,
  SectionDivide,
  SectionTitle
} from '../../Program/components/learning-lifecycle/panelSections';
import { MathRow, Op, TIP_HEADING, TIP_TEXT } from '../../Program/components/learning-lifecycle/TeamCapabilityScore';
import {
  capabilityScore,
  LEVEL_ORDER,
  QUIZ_DROP_AT_PERCENT,
  RECENT_COUNT,
  scoreCounts,
  type CapabilityLevel,
  type CapabilityPair,
  type RoleplayResult
} from '../../Program/components/learning-lifecycle/teamCapability';
import { GRADED_PER_WEEK, levelChange } from '../../capability-setup/learner/learnerStore';
import { AnalysisDialog, PRACTICE_ATTEMPTS, RoleplayDialog } from '../../capability-setup/learner/RoleplayDialogs';
import type { CapabilityMeasureView, TrendView } from '../../../../../api/capability';

/** The tab's data, from the backend: the person's role and every capability of it. */
export interface CapabilityTabData {
  roleName: string;
  measures: CapabilityMeasureView[];
}

const rank = (level: CapabilityLevel | null) => LEVEL_ORDER.indexOf(level as CapabilityLevel);
const levelText = (level: CapabilityLevel) => ({ text: LEVEL_NAME[level], color: LEVEL_COLOR[level] });

/** The four levels, the learner's one picked out: what a single capability's hover shows. */
const levelTooltip = (pair: CapabilityPair) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 230 }}>
    <Typography sx={{ ...TIP_TEXT, whiteSpace: 'nowrap' }}>Your level on this capability.</Typography>
    <Box>
      <Typography sx={TIP_HEADING}>Levels</Typography>
      {LEVEL_ORDER.map((level) => (
        <MathRow
          key={level}
          label={
            <Box component="span" sx={{ color: LEVEL_COLOR[level], fontWeight: level === pair.level ? 700 : 600 }}>
              {LEVEL_NAME[level]}
              {level === pair.level ? ' (now)' : ''}
            </Box>
          }
          value={level === 'capable' || level === 'advanced' ? 'Meets the bar' : 'Below the bar'}
        />
      ))}
    </Box>
  </Box>
);

/** Up, down or level with where they were before their most recent Roleplay; nothing when there is no "before". */
const ChangeMark: React.FC<{ measure: CapabilityMeasureView }> = ({ measure }) => {
  const change = levelChange(measure.previousLevel, measure.working.level);
  if (!change) return null;
  // In the colour of the level they are at now, whichever way they got there.
  const sx = { fontSize: 14, display: 'block', color: LEVEL_COLOR[measure.working.level as CapabilityLevel] };
  return (
    <DosenTooltip title={change.text} placement="bottom">
      <Box component="span" aria-label={change.text} sx={{ flexShrink: 0, display: 'inline-flex', cursor: 'default' }}>
        {change.direction === 'up' && <NorthIcon sx={sx} />}
        {change.direction === 'down' && <SouthIcon sx={sx} />}
        {change.direction === 'same' && <RemoveIcon sx={sx} />}
      </Box>
    </DosenTooltip>
  );
};

/**
 * One of the two ways to work on a capability: a button in the capability
 * row's own brandy frame and tint. Hovering it says what it does.
 */
const ImproveAction: React.FC<{
  label: string;
  tip: string;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, tip, primary = false, disabled = false, onClick }) => (
  <DosenTooltip title={tip} placement="bottom" componentsProps={{ tooltip: { sx: { maxWidth: 300 } } }}>
    {/* The span takes the hover, since a disabled button gives none. */}
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <DosenButton
        tone={primary ? 'cta' : 'basic'}
        sizeVariant="sm"
        disabled={disabled}
        onClick={onClick}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {label}
      </DosenButton>
    </Box>
  </DosenTooltip>
);

/**
 * One of the learner's capabilities: how their level moved and where it is
 * now, opening onto what they can do about it and the Roleplays and Quiz
 * Questions that set it.
 */
const MeasureRow: React.FC<{
  measure: CapabilityMeasureView;
  roleName: string;
  open: boolean;
  /** Graded Roleplays they can still start on this capability this week. */
  gradedLeft: number;
  onToggle: () => void;
  onStart: (kind: 'graded' | 'practice') => void;
  onAnalysis: (roleplay: RoleplayResult) => void;
  /** An admin looking at this member: their name, in place of "you", and no Roleplays to start. */
  memberName?: string;
}> = ({ measure, roleName, open, gradedLeft, onToggle, onStart, onAnalysis, memberName }) => (
  <Box
    data-testid="capability-measure"
    data-capability={measure.capabilityName}
    sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, ...fadeInSx }}
  >
    <Box
      onClick={onToggle}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(247, 148, 32, 0.45)',
        bgcolor: 'rgba(247, 148, 32, 0.06)',
        borderRadius: '4px',
        pl: '12px',
        pr: '10px',
        py: '2px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
        minHeight: 36,
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(247, 148, 32, 0.12)' }
      }}
    >
      <DosenTooltip title={measure.capabilityName} placement="bottom-start" enterDelay={300}>
        <Typography
          sx={{
            // The name takes all the room the rest leaves, so it is cut only when nothing else can give.
            flex: '1 1 auto',
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            textTransform: 'uppercase',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0
          }}
        >
          {measure.capabilityName}
        </Typography>
      </DosenTooltip>
      <SourceChip compact source={sourceOf(measure)} />
      {/* The role sits against the level, and is the first to give when the row is tight. */}
      <Typography
        sx={{
          flex: '0 1000 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'right',
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          color: '#B6B6B6',
          whiteSpace: 'nowrap'
        }}
      >
        {roleName}
      </Typography>
      {/* How the level moved with the most recent Roleplay, then the level. Not assessed yet is a dash. */}
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px'
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ChangeMark measure={measure} />
          <LevelLabel level={measure.working.level} />
          {measure.working.onPreviousLevels && (
            <PreviousLevelsMark
              reassess={
                memberName
                  ? `${memberName}'s next counted Roleplay measures them on the current ones.`
                  : 'Your next Graded Roleplay measures you on the current ones.'
              }
            />
          )}
        </Box>
        <IconButton
          aria-label={open ? `Collapse ${measure.capabilityName}` : `Expand ${measure.capabilityName}`}
          sx={{ flexShrink: 0, width: 22, height: 22, p: 0, color: '#FDFDFD' }}
        >
          {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>
    </Box>

    <Collapse in={open} unmountOnExit>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* What to do next on this capability: practise, or assess themselves. Theirs alone to start. */}
        {!memberName && (
          <GapSection title="Improve this capability">
            {measure.working.onPreviousLevels && (
              <Typography sx={{ ...BODY, textAlign: 'center', pb: '4px' }}>
                The levels for this capability have been updated. Take a Scored Roleplay to see your level on the new
                ones.
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                py: '4px'
              }}
            >
              <ImproveAction
                label="Practice Roleplay"
                tip={`Not graded, and never counts toward your level. Up to ${PRACTICE_ATTEMPTS} attempts, with Elliott's feedback after each.`}
                onClick={() => onStart('practice')}
              />
              <ImproveAction
                label="Scored Roleplay"
                primary
                tip={`A self-assessment: one attempt, graded by Elliott against this capability's levels. It counts toward your level. ${gradedLeft} of ${GRADED_PER_WEEK} left this week.`}
                disabled={gradedLeft === 0}
                onClick={() => onStart('graded')}
              />
            </Box>
          </GapSection>
        )}
        <GapSection title={memberName ? 'How the level is worked out' : 'How your level is worked out'}>
          <HistoryTables
            working={measure.working}
            who={memberName ? `${memberName} has` : 'You have'}
            whose={memberName ? 'their' : 'your'}
            onAnalysis={onAnalysis}
          />
        </GapSection>
      </Box>
    </Collapse>
  </Box>
);

/**
 * The learner's Capability tab, in the admin tab's three sections: their own
 * score (the share of their role's capabilities at Capable or better) with
 * their strongest and weakest capability, the Last 6 Months chart, then their
 * Capability Levels: every capability of their role with their level, lowest
 * first. Each opens onto what they can do next (practise, or take a graded
 * Roleplay) and the Roleplays and Quiz Questions that set the level, with
 * Elliott's analysis of each Roleplay.
 */
const MyCapabilityTab: React.FC<{
  capability: CapabilityTabData | null;
  /** Their capability % week by week, from the backend. */
  trend?: TrendView | null;
  /** An admin's view of one member (the Members table): third person, and nothing to start. */
  member?: { id: string; name: string };
  /** A Roleplay they started has been recorded: read their levels again. */
  onChanged?: () => void;
  /**
   * The learner holds no role: 'choose' when a team of theirs still waits for
   * them to pick their role on their profile, 'waiting' when they answered "My
   * role isn't listed" and their role isn't set up yet.
   */
  noRole?: 'choose' | 'waiting' | null;
}> = ({ capability, trend: trendView, member, onChanged, noRole = null }) => {
  const navigate = useNavigate();
  const memberName = member?.name;
  const [openMeasure, setOpenMeasure] = useState<string | null>(null);
  const [starting, setStarting] = useState<{ measure: CapabilityMeasureView; kind: 'graded' | 'practice' } | null>(
    null
  );
  const [analysing, setAnalysing] = useState<{ measure: CapabilityMeasureView; roleplay: RoleplayResult } | null>(null);
  const trend = useMemo(() => trendFromApi(trendView), [trendView]);

  // No role, so no capabilities: every section stays, with nothing in it yet ("-", a chart with no
  // line) and, where the capabilities would be, what has to happen first.
  if (!capability) {
    const DASH = { text: '-', color: '#868686' };
    return (
      <>
        <ScoreCard percent={null} tier={DASH}>
          <ScoreLine label="Strongest Capability" name="" percent={null} tier={DASH} />
          <ScoreLine label="Weakest Capability" name="" percent={null} tier={DASH} />
        </ScoreCard>
        <SectionDivide />
        <ChartSection>
          <CapabilityTrend
            compact
            title="Last 6 Months"
            window={trend.window}
            series={trend.series.map((s) => ({ ...s, points: s.points.map(() => null) }))}
            events={[]}
          />
        </ChartSection>
        <SectionDivide />
        <SectionTitle>Capability</SectionTitle>
        <Typography data-testid="no-role-capabilities" sx={{ ...BODY, color: '#868686' }}>
          {memberName ? (
            `${memberName} has no role yet, so has no required capabilities.`
          ) : noRole === 'choose' ? (
            <>
              Choose your role on your profile to see your required capabilities.{' '}
              <Box
                component="button"
                type="button"
                onClick={() => navigate('/app/profile')}
                sx={{
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  color: '#F79420',
                  font: 'inherit',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Go to Your Profile
              </Box>
            </>
          ) : (
            "Your role's required capabilities will appear here once confirmed by your manager."
          )}
        </Typography>
      </>
    );
  }

  const { measures, roleName } = capability;
  // One score per capability of their role, as the dashboards count them.
  const pairs: CapabilityPair[] = measures.map((m) => ({
    personId: member?.id || 'me',
    teamId: 'team',
    roleId: 'role',
    roleName,
    capabilityId: m.id,
    capabilityName: m.capabilityName,
    level: m.working.level
  }));
  const score = capabilityScore(pairs);
  const counts = scoreCounts(pairs);
  const assessed = pairs.filter((p) => p.level !== null);
  // Ties go to the first in the list.
  const strongest = assessed.reduce<CapabilityPair | null>(
    (best, p) => (!best || rank(p.level) > rank(best.level) ? p : best),
    null
  );
  const weakest = assessed.reduce<CapabilityPair | null>(
    (worst, p) => (!worst || rank(p.level) < rank(worst.level) ? p : worst),
    null
  );
  const { series, events } = trend;

  const scoreTooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 280 }}>
      <Box>
        <Typography sx={TIP_HEADING}>Breakdown</Typography>
        <MathRow label={`Capabilities for ${roleName}`} value={counts.scores} />
        {counts.notAssessed > 0 && (
          <MathRow
            label="Not Yet Assessed"
            value={
              <>
                <Op>−</Op>
                {counts.notAssessed}
              </>
            }
          />
        )}
        <MathRow ruleAbove label="Assessed" value={score.total} />
        <MathRow label="Capable or Better" value={score.count} />
        {score.percent !== null && (
          <MathRow
            total
            label={memberName ? 'Capability' : 'My Capability'}
            value={
              <>
                {score.count}
                <Op>÷</Op>
                {score.total}
                <Op>=</Op>
                {score.percent}%
              </>
            }
          />
        )}
      </Box>
      <Typography sx={{ ...TIP_HEADING, mt: '6px' }}>Scoring</Typography>
      <Typography sx={TIP_TEXT}>
        Elliott grades each Roleplay as weak, developing, capable or advanced, based on the criteria set by{' '}
        {memberName ? 'their' : 'your'} Team Owner. {memberName ? 'Their' : 'Your'} level on a capability is the average
        of {memberName ? 'their' : 'your'} {RECENT_COUNT} most recent Roleplays on it, rounded down. Passing{' '}
        {QUIZ_DROP_AT_PERCENT}% or fewer of {memberName ? 'their' : 'your'} {RECENT_COUNT} most recent Quiz Questions
        lowers it by one level.
      </Typography>
    </Box>
  );

  return (
    <>
      <ScoreCard percent={score.percent} tooltip={scoreTooltip}>
        {strongest && (
          <ScoreLine
            label="Strongest Capability"
            name={strongest.capabilityName}
            percent={null}
            tier={levelText(strongest.level as CapabilityLevel)}
            tooltip={levelTooltip(strongest)}
          />
        )}
        {weakest && weakest !== strongest && (
          <ScoreLine
            label="Weakest Capability"
            name={weakest.capabilityName}
            percent={null}
            tier={levelText(weakest.level as CapabilityLevel)}
            tooltip={levelTooltip(weakest)}
          />
        )}
      </ScoreCard>
      <SectionDivide />

      <ChartSection>
        <CapabilityTrend compact title="Last 6 Months" window={trend.window} series={series} events={events} />
      </ChartSection>
      <SectionDivide />

      <SectionTitle>Capability</SectionTitle>
      {measures.length === 0 ? (
        <Typography sx={{ ...BODY, color: '#868686' }}>
          {memberName
            ? `${memberName}'s capabilities will appear here as soon as their Manager sets them for their role.`
            : 'Your capabilities will appear here as soon as your Manager sets them for your role.'}
        </Typography>
      ) : (
        measures.map((measure) => (
          <MeasureRow
            key={measure.id}
            measure={measure}
            roleName={roleName}
            memberName={memberName}
            open={openMeasure === measure.id}
            gradedLeft={measure.gradedLeftThisWeek ?? GRADED_PER_WEEK}
            onToggle={() => setOpenMeasure((prev) => (prev === measure.id ? null : measure.id))}
            onStart={(kind) => setStarting({ measure, kind })}
            onAnalysis={(roleplay) => setAnalysing({ measure, roleplay })}
          />
        ))
      )}

      <AnalysisDialog
        capabilityName={analysing?.measure.capabilityName ?? ''}
        roleplay={analysing?.roleplay ?? null}
        criteria={analysing?.measure.criteria ?? {}}
        onClose={() => setAnalysing(null)}
      />
      {starting && (
        <RoleplayDialog
          open
          kind={starting.kind}
          capabilityId={starting.measure.id}
          capabilityName={starting.measure.capabilityName}
          criteria={starting.measure.criteria}
          onClose={() => setStarting(null)}
          onFinished={() => onChanged?.()}
        />
      )}
    </>
  );
};

export default MyCapabilityTab;
